/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import get from 'lodash/get';
import Typography from 'carbon-react/lib/components/typography';
import Button from 'carbon-react/lib/components/button';
import Textbox from 'carbon-react/lib/components/textbox';

import { FormattedMessage, IntlShape, useIntl } from '@futrli/localisation';
import { UILoaderSpinner } from '@futrli/ui-components';
import { isPending, Logger, ONLY_DIGITS } from '@futrli/shared-services';

import * as styles from '../my-profile.scss';
import { AuthError, StoreState } from '../../core/types';
import { setPreferredMFAType, updateUserAttributes, verifyUserAttributesSubmit } from '../../auth/redux-utils/actions';
import { setUsersMfa } from '../my-profile-routes';
import { getErrorSelector } from '../../core/redux-utils/selectors';
import { VERIFY_USER_ATTRIBUTES_SUBMIT } from '../../auth/constants';
import { resetError } from '../../core/redux-utils/actions';

export const VERIFICATION_CODE_LENGTH = 6;

export const MIN_PHONE_NUMBER_LENGTH = 10;

interface Props {
  handleSwitchClick: (value: boolean) => void;
}

const getVerificationCodeLabel = (submittedPhoneNumber: boolean, intl: IntlShape) => {
  if (submittedPhoneNumber) {
    return intl.formatMessage({
      id: 'login.sms_sent',
      defaultMessage: 'An SMS with a code has been sent to the number above'
    });
  }

  return intl.formatMessage({ id: 'common.verification_code', defaultMessage: 'Verification code' });
};

export const EnableMfa = ({ handleSwitchClick }: Props) => {
  const [existingPhoneNumber, setExistingPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [submittedPhoneNumber, setSubmittedPhoneNumber] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [submitError, setSubmitError] = useState<AuthError | null>(null);

  const {
    user,
    error: verificationError,
    isLoadingVerifyUserAttrSubmit,
    isLoadingSetPreferredMfaType,
    cognitoUser
  } = useSelector((state: StoreState) => ({
    user: state.authReducers.authenticatedUser,
    cognitoUser: state.authReducers.userAttributes,
    error: getErrorSelector(state.authReducers, VERIFY_USER_ATTRIBUTES_SUBMIT.ACTION),
    isLoadingVerifyUserAttrSubmit: isPending(state.loadingRequests.VERIFY_USER_ATTRIBUTES_SUBMIT),
    isLoadingSetPreferredMfaType: isPending(state.loadingRequests.SET_PREFERRED_MFA_TYPE)
  }));

  const dispatch = useDispatch();

  useEffect(() => {
    setSubmitError(verificationError);
  }, [verificationError]);

  useEffect(() => {
    dispatch(resetError(VERIFY_USER_ATTRIBUTES_SUBMIT));
  }, [dispatch]);

  useEffect(() => {
    const authUserPhoneNumber = get(user, 'attributes.phone_number', '');
    const cognitoUserPhoneNumber = get(cognitoUser, 'phone_number', '');
    setExistingPhoneNumber(authUserPhoneNumber || cognitoUserPhoneNumber);
  }, [user, cognitoUser]);

  const intl = useIntl();

  const handlePhoneNumberChange = (newPhoneNumber: string) => {
    setPhoneNumber(newPhoneNumber);
    if (phoneNumberError) {
      setPhoneNumberError(false);
    }
  };

  const handleVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(event.target.value);
    if (submitError) {
      setSubmitError(null);
    }
  };

  const setInitialValues = () => {
    setPhoneNumber('');
    setSubmittedPhoneNumber(false);
    setVerificationCode('');
    setSubmitError(null);
    handleSwitchClick(false);
  };

  const handleCancelClick = () => {
    setInitialValues();
  };

  const handleSubmitPhoneNumber = async () => {
    try {
      setIsLoading(true);
      setSubmittedPhoneNumber(false);
      if (existingPhoneNumber) {
        await updateUserAttributes({ phone_number: '' })(dispatch);
      }
      await updateUserAttributes({ phone_number: `+${phoneNumber}` })(dispatch);
      setSubmittedPhoneNumber(true);
    } catch (e) {
      Logger.error(e as string);
      setPhoneNumberError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitVerifyCode = async () => {
    try {
      setIsLoading(true);
      await verifyUserAttributesSubmit('phone_number', verificationCode)(dispatch);
      await setUsersMfa(user.username);
      await setPreferredMFAType()(dispatch);
      setInitialValues();
    } catch (e) {
      Logger.error(e as string);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidVerificationCode =
    ONLY_DIGITS.test(verificationCode) && verificationCode.length === VERIFICATION_CODE_LENGTH;

  const isValidPhoneNumber = phoneNumber.length >= MIN_PHONE_NUMBER_LENGTH;

  const isEnableMfaDisabled = !submittedPhoneNumber || !isValidVerificationCode || !!submitError || isLoading;

  if (isLoading || isLoadingVerifyUserAttrSubmit || isLoadingSetPreferredMfaType) {
    return <UILoaderSpinner />;
  }

  return (
    <div>
      <Typography mb="15px" fontWeight="700" fontSize="18px">
        <FormattedMessage id="my_profile.turn_on_mfa" defaultMessage="Turn on multi-factor authentication" />
      </Typography>
      <Typography fontWeight="500" mb={0}>
        <FormattedMessage id="my_profile.enter_phone_number" defaultMessage="Enter phone number" />
      </Typography>
      <Typography>
        <FormattedMessage
          id="my_profile.enter_phone_number_desc"
          defaultMessage="Please enter your cell number (including country code e.g. +44) below to verify your account so we can send you a verification code."
        />
      </Typography>
      <PhoneInput
        inputStyle={{ fontSize: 14 }}
        inputClass={styles.phoneInput}
        buttonClass={styles.phoneButtonClass}
        disableDropdown={false}
        value={phoneNumber}
        onChange={value => handlePhoneNumberChange(value)}
        // TODO i18n: country should come from locale settings
        country={'gb'}
        preferredCountries={['gb', 'au', 'nz', 'ca', 'us']}
        preserveOrder={['preferredCountries']}
        isValid={() => {
          if (phoneNumberError) {
            return false;
          }
          return true;
        }}
        defaultErrorMessage={intl.formatMessage({
          id: 'change_password.attempt_limit_exceeded',
          defaultMessage: 'Attempt limit exceeded, please try after some time.'
        })}
      />
      <div style={{ display: 'flex', marginBottom: 20 }}>
        <Button
          disabled={!isValidPhoneNumber || isLoading || submittedPhoneNumber}
          onClick={handleSubmitPhoneNumber}
          ml="auto"
          buttonType="primary"
          size="medium"
        >
          <FormattedMessage id="my_profile.send_code" defaultMessage="Send code" />
        </Button>
      </div>
      <Textbox
        label={getVerificationCodeLabel(submittedPhoneNumber, intl)}
        placeholder={intl.formatMessage({
          id: 'my_profile.enter_verification_code_here',
          defaultMessage: 'Enter verification code here'
        })}
        value={verificationCode}
        onChange={handleVerificationCodeChange}
        disabled={!submittedPhoneNumber}
        error={
          submitError
            ? intl.formatMessage({
                id: 'my_profile.invalid_verification_code',
                defaultMessage: 'Invalid verification code provided, please try again.'
              })
            : ''
        }
        size="large"
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', marginTop: 30 }}>
          <Button buttonType="secondary" onClick={handleCancelClick} mr="12px" size="medium">
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button buttonType="primary" disabled={isEnableMfaDisabled} onClick={handleSubmitVerifyCode} size="medium">
            <FormattedMessage id="my_profile.enable_mfa" defaultMessage="Enable MFA" />
          </Button>
        </div>
      </div>
    </div>
  );
};
